// @ts-nocheck
// TypeScript is ignoring this file. If you are editing this file you must fix the type issues.

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LiliButton } from '@lili/lili-frontend-common';
import Heading from '../../../../../generic-components/Heading';
import { VIEWS_MAP } from '../../../../../router/constants';
import { useStore } from '../../../../../store';
import { NavigationHeader } from '../../../../../components/NavigationHeader';
import { convertValueToUSD } from '../../../../../helpers/currency.helper';
import { APP_LAYOUT_STYLES } from '../../../../../generic-components/Layout/constants';
import { STYLES } from '../consts';
import AppLayout from '../../../../../generic-components/Layout';
import { getVendorDetailsBlocks, MoveMoneyTypes } from '../../../../MoveMoney/consts';
import InfoIcon from '../../../../../assets/svg/info-blue.svg';
import Icon from '../../../../../generic-components/Icon';
import { LiliCheckbox, LiliCheckboxType } from '../../../../../DesignSystem/LiliCheckbox/LiliCheckbox';
import { postRequestVendorDetails } from '../hooks/postRequestVendorDetails';
import { IBillItem } from '../../../types';
import LiliToast, { LiliToastDefaultConfig } from '../../../../../DesignSystem/LiliToast/LiliToast';
import { useUploadGenericFile } from '../../../../../hooks/utilities/useUploadGenericFile';
import { GENERIC_FILE_UPLOAD_TYPES } from '../../../../../types';
import { trackMixPanel } from '../../../../../services/mixpanel.service';
import LiliNotification, { LiliNotificationType } from '../../../../../DesignSystem/LiliNotification/LiliNotification';

const AskYourVendorReview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resetMoveMoneyData = useStore((state) => state.resetMoveMoneyData);
  const moveMoneyData = useStore((state) => state.moveMoneyData);
  const moveType = MoveMoneyTypes.ASK_VENDOR;
  const updateNewItemByExternalId = useStore((state) => state.updateNewItemByExternalId);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isScreenShotUploaded, setIsScreenShotUploaded] = useState<boolean>(false);
  const {
    state: { email },
  } = useLocation();
  const { getPrimaryAccount } = useStore((state) => state);
  const { mutateAsync: postUploadScreenShot } = useUploadGenericFile();
  const handleCloseFlow = () => {
    trackMixPanel('Act On Ask Vendor Review and Confirm Screen', {
      Action: 'Close',
    });
    resetMoveMoneyData();
    navigate(`/${VIEWS_MAP.DASHBOARD}`);
  };

  /** Queries */
  const { mutateAsync: requestVendorDetails, isLoading: isLoadingRequestVendorDetails } = postRequestVendorDetails(
    (billItem: IBillItem) => {
      resetMoveMoneyData();
      updateNewItemByExternalId(billItem);
      navigate(`/${VIEWS_MAP.BILLS.BASE}`);
      toast(<LiliToast text={t('ASK_YOUR_VENDOR.SUCCESS')} id='requestSentSuccess' />, { ...LiliToastDefaultConfig });
    },
    (errData) => {
      toast(
        <LiliNotification
          type={LiliNotificationType.ERROR}
          text={errData.response.data.errors[0].localizedMessage}
          id='requestSentFailure'
        />,
        { ...LiliToastDefaultConfig },
      );
    },
  );
  /** ------- */

  useEffect(() => {
    trackMixPanel('View Ask Vendor Review and Confirm Screen');
  }, []);

  const handleCheckboxChange = (checked: boolean) => {
    setIsChecked(checked);
  };

  const onSubmit = () => {
    trackMixPanel('Act On Ask Vendor Review and Confirm Screen', {
      Action: 'Confirm and Send',
    });
    requestVendorDetails({ billId: moveMoneyData.billId, email });
  };

  useEffect(() => {
    if (isChecked && !isScreenShotUploaded) {
      postUploadScreenShot({
        fileUsageType: GENERIC_FILE_UPLOAD_TYPES.ASK_VENDOR_FOR_DETAILS,
        imageName: `${Date.now()}`,
      }).then(() => setIsScreenShotUploaded(true));
    }
  }, [isChecked]);

  useEffect(() => {
    if (!moveMoneyData.billId || !email) {
      navigate(`/${VIEWS_MAP.BILLS.BASE}`);
    }
  }, []);

  return (
    <AppLayout
      layoutStyle={APP_LAYOUT_STYLES.CHOOSE_PAYMENT_METHOD.MAIN_LAYOUT_CHOOSE_METHOD}
      navBarStyle={`${APP_LAYOUT_STYLES.CHOOSE_PAYMENT_METHOD.NAVBAR_LAYOUT} gap-0`}
      hideCustomerProfile
      chatBotStyle='hidden'
    >
      <div className='h-full text-center flex flex-col w-full items-center justify-between'>
        <NavigationHeader
          onCloseAction={handleCloseFlow}
          pageTitle={t('ASK_YOUR_VENDOR.PAGE_TITLE')}
          layoutStyle={STYLES.NAVIGATION_HEADER}
        />
        <div className='w-[432px]'>
          <Heading
            title={t('ASK_YOUR_VENDOR.REVIEW_TITLE')}
            className='font-metricMedium text-[24px] text-bw08 mt-[64px]'
          />
          <div className='py-[16px] px-[16px] rounded-[8px] border border-bw03 w-full mx-auto mb-[10px] mt-[16px]'>
            <Heading
              title={t('MOVE_MONEY.STEPS.REVIEW.TRANSFER_DETAILS')}
              className='flex items-start font-metricRMedium text-bw08 text-[18px] mb-[14px]'
            />
            <div className='flex items-center justify-between w-full pb-[16px] border-b border-bw03 mb-[16px]'>
              <Heading title={t('MOVE_MONEY.STEPS.REVIEW.AMOUNT')} className='font-metricR text-bw07 text-[16px]' />
              <div className='flex items-center gap-[6px]'>
                <Heading
                  title={convertValueToUSD(moveMoneyData.amount, 2)}
                  className='font-metricRMedium text-action03 text-[22px]'
                />
              </div>
            </div>

            <Heading
              title={t('MOVE_MONEY.STEPS.REVIEW.MAIL.DYNAMIC_BLOCKS_TITLE')}
              className='flex items-start font-metricRMedium text-bw08 text-[18px] mb-[10px]'
            />
            <div className='flex flex-col gap-[10px]'>
              {getVendorDetailsBlocks(moveType, moveMoneyData).map((item) => (
                <div key={`${item.title}`} className='flex items-start justify-between'>
                  <Heading title={t(item.title)} className='font-metricR text-bw07 text-[14px]' />
                  <Heading
                    title={t(item.value)}
                    className='font-metricR max-w-[150px] text-wrap break-all text-right text-bw08 text-[14px]'
                  />
                </div>
              ))}
              <div className='flex items-start border-t pt-[16px] border-bw03 justify-between'>
                <Heading title='Fee' className='font-metricR text-bw06 text-[16px]' />
                <Heading title={convertValueToUSD(0, 0)} className='font-metricR text-bw06 text-[16px]' />
              </div>
            </div>
          </div>
          <div className='flex flex-col items-start mt-[16px]'>
            <div className='flex flex-row'>
              <Icon iconId='infoIcon' src={InfoIcon} className='w-[20px] h-[20px] mr-[2px]' />
              <Heading
                title={t('MOVE_MONEY.STEPS.REVIEW.ASK_VENDOR.DISCLAIMER_1')}
                className='font-metricR text-bw08 text-[14px]'
              />
            </div>
            <div className='flex flex-row mt-[6px]'>
              <Icon iconId='infoIcon' src={InfoIcon} className='w-[20px] h-[20px] mr-[2px]' />
              <Heading
                title={t('MOVE_MONEY.STEPS.REVIEW.ASK_VENDOR.DISCLAIMER_2')}
                className='font-metricR text-bw08 text-[14px]'
              />
            </div>
          </div>
          <div className='flex items-start gap-[8px] pt-[16px]'>
            <LiliCheckbox
              id='consentAskVendorCheckbox'
              type={LiliCheckboxType.REGULAR}
              checked={isChecked}
              onCheckedChange={handleCheckboxChange}
            />
            <button type='button' onClick={() => handleCheckboxChange(!isChecked)}>
              <Heading
                title={t('ASK_YOUR_VENDOR.CONSENT', {
                  amount: convertValueToUSD(moveMoneyData.amount, 2),
                  vendor: moveMoneyData.vendorData?.name,
                  bank: getPrimaryAccount().bankInfo.bankInstInfo,
                })}
                className='font-metricR text-bw07 text-[14px] text-left'
              />
            </button>
          </div>
        </div>
      </div>
      <div className='flex flex-col w-full items-center py-6'>
        <div className='w-full border-t border-bw03 pb-6' />
        <LiliButton
          isLoading={isLoadingRequestVendorDetails}
          onClick={onSubmit}
          disabled={!isChecked}
          id='notifyVendor'
          className='w-[375px]'
          title={t('MOVE_MONEY.STEPS.SUCCESS.NOTIFY_VENDOR.CONFIRM_AND_SEND')}
        />
      </div>
    </AppLayout>
  );
};

export default AskYourVendorReview;
